/**
 * The route paths (spec §13): the addressing contract deep links and every page rely on.
 *
 * Why a leaf module: routes.tsx imports every page component, and the pages import these paths.
 * Declared inside routes.tsx, PATHS was still uninitialised while a page module evaluated, so a
 * module-level use of it (a crumb constant, say) crashed the whole SPA at boot. A leaf with no
 * imports of its own cannot take part in that cycle; routes.tsx re-exports it for convenience.
 */
export const PATHS = {
  overview: '/',
  todos: '/todos',
  meetings: '/meetings',
  meeting: (id: string) => `/meetings/${encodeURIComponent(id)}`,
  projects: '/projects',
  project: (id: string) => `/projects/${encodeURIComponent(id)}`,
  assistant: '/assistant',
  /**
   * The executive module (ADR 0019; design §2.13): four URL-addressed sections — the report (the latest, or one by its id
   * in `?report=`), the pulse, the shared matters and coverage.
   */
  executive: '/executive',
  executiveReport: (id: string) => `/executive?report=${encodeURIComponent(id)}`,
  executivePulse: '/executive/pulse',
  executiveMatters: '/executive/matters',
  executiveCoverage: '/executive/coverage',
  connections: '/connections',
  /** A system's run history (06-modules §6.3 R2): `fellow` or `slack`, arriving with the system identified. */
  connectionRuns: (system: 'fellow' | 'slack') => `/connections/${system}/runs`,
  settings: '/settings',
  admin: '/admin',
  adminUsers: '/admin/users',
  adminUser: (id: string) => `/admin/users/${encodeURIComponent(id)}`,
  adminGroups: '/admin/groups',
  adminGroup: (id: string) => `/admin/groups/${encodeURIComponent(id)}`,
  adminAudit: '/admin/audit',
  adminCosts: '/admin/costs',
  adminQueue: '/admin/queue',
  signedOut: '/signed-out',
  devSignIn: '/dev-sign-in',
} as const;

/**
 * The query parameters that name the record a page shows, by the page's path: a change of one is a change of page, as a
 * change of path is (02-patterns §2.4: whether a page's address "is a route segment or a query parameter is the app's own
 * affair"). `RouteFocus` reads it, so opening another record moves focus to the heading and the scroll to the top
 * (02-patterns §4), while a sort or a filter in the query does not. Why the executive report (ADR 0019; the stage 6
 * review): `/executive?report=<id>` is one report's address, and moving from one report to another changed nothing else.
 */
export const RECORD_PARAMS: Readonly<Record<string, string>> = Object.freeze({ [PATHS.executive]: 'report' });

